import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Clock, LogIn, LogOut, Coffee, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useTodayLog, useAttendanceMutations } from '../hooks/useAttendance';
import { BREAK_OPTIONS, type BreakType } from '../types';
import { formatClock, formatDuration, minutesOnDuty } from '../utils';

/**
 * Always-visible attendance control embedded in the top bar. A compact status
 * pill (Check In / On break / Checked in · Xh / Checked out) opens a square
 * control box with the full set of quick actions — check in, breaks
 * (Lunch/Tea/Restroom/Meeting), end break, and check out — so attendance is one
 * click away from every page. Reuses the existing `/api/time-logs/*` endpoints;
 * no backend change.
 */
export function AttendanceWidget() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const { data: today, isLoading } = useTodayLog();
  const { checkIn, checkOut, startBreak, endBreak } = useAttendanceMutations();
  const busy = checkIn.isPending || checkOut.isPending || startBreak.isPending || endBreak.isPending;

  const log = today?.log ?? null;
  const activeBreak = today?.activeBreak ?? null;
  const checkedIn = !!log?.check_in;
  const checkedOut = !!log?.check_out;
  const onBreak = !!activeBreak;

  // Live time on duty — re-drawn every minute while checked in.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!checkedIn || checkedOut) return;
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [checkedIn, checkedOut]);
  const worked = checkedIn ? formatDuration(minutesOnDuty(log?.check_in, log?.check_out)) : '';

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const status = useMemo(() => {
    if (checkedOut) return { label: `Checked out · ${worked}`, tone: 'info' as const, dot: 'bg-info' };
    if (onBreak) return { label: `On break · ${worked}`, tone: 'warning' as const, dot: 'bg-warning animate-pulse' };
    if (checkedIn) return { label: `On duty · ${worked}`, tone: 'success' as const, dot: 'bg-success' };
    return { label: 'Check in', tone: 'neutral' as const, dot: 'bg-white' };
  }, [checkedIn, checkedOut, onBreak, worked]);

  const run = (
    m: { mutate: (variables: void, options?: { onSuccess?: () => void; onError?: (e: unknown) => void }) => void },
    ok: string,
  ) =>
    m.mutate(undefined, {
      onSuccess: () => toast.success(ok),
      onError: (e) => toast.error(getApiErrorMessage(e, 'Action failed.')),
    });

  return (
    <div className="relative" ref={ref}>
      {/* Trigger pill */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-full border px-3 text-xs font-bold transition-colors',
          !checkedIn
            ? 'border-success/40 bg-success text-white shadow-sm hover:bg-success'
            : 'border-outline text-on-surface hover:bg-surface-variant',
          open && checkedIn && 'bg-surface-variant',
        )}
        title="Attendance"
      >
        <span className={cn('h-2 w-2 rounded-full', status.dot)} />
        <Clock className={cn('h-4 w-4', checkedIn ? 'text-on-surface-variant' : 'text-white')} />
        <span className={cn('max-w-[160px] truncate', checkedIn ? 'hidden md:inline' : 'inline')}>{status.label}</span>
        <ChevronDown className={cn('h-3.5 w-3.5', checkedIn ? 'text-on-surface-variant' : 'text-white')} />
      </button>

      {/* Square control box */}
      {open && (
        <div
          role="dialog"
          aria-label="Attendance controls"
          className="absolute right-0 top-11 z-40 w-72 rounded-card border border-outline bg-surface p-4 shadow-surface-3"
        >
          {isLoading ? (
            <div className="flex h-32 items-center justify-center"><Spinner /></div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-data-lg font-semibold text-on-surface">Attendance</span>
                </div>
                <Badge tone={status.tone}>
                  {checkedOut ? 'Checked Out' : onBreak ? 'On Break' : checkedIn ? 'Checked In' : 'Not In'}
                </Badge>
              </div>

              {/* Times */}
              <div className="grid grid-cols-3 gap-2">
                <Metric label="In" value={formatClock(log?.check_in)} />
                <Metric label="Out" value={formatClock(log?.check_out)} />
                <Metric label="Worked" value={worked || '—'} />
              </div>

              {onBreak && (
                <div className="flex items-center gap-2 rounded-card-sm border border-warning/30 bg-warning/10 px-2.5 py-1.5 text-body-sm text-on-surface">
                  <Coffee className="h-4 w-4 text-warning-text" /> On break ({activeBreak?.break_type})
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-outline pt-3">
                {!checkedIn && (
                  <Button className="w-full justify-center" loading={busy} onClick={() => run(checkIn, 'Checked in.')}>
                    <LogIn className="h-4 w-4" /> Check In
                  </Button>
                )}

                {checkedIn && !checkedOut && (
                  <div className="flex flex-col gap-2.5">
                    {onBreak ? (
                      <Button variant="secondary" className="w-full justify-center" loading={busy} onClick={() => run(endBreak, 'Break ended.')}>
                        <Coffee className="h-4 w-4" /> End Break
                      </Button>
                    ) : (
                      <div>
                        <p className="mb-1.5 text-label-caps uppercase text-on-surface-variant">Start a break</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {BREAK_OPTIONS.map((b) => (
                            <Button
                              key={b.value}
                              variant="outline"
                              size="sm"
                              className="justify-center"
                              disabled={busy}
                              onClick={() =>
                                startBreak.mutate(b.value as BreakType, {
                                  onSuccess: () => toast.success(`${b.label} break started.`),
                                  onError: (e) => toast.error(getApiErrorMessage(e, 'Could not start break.')),
                                })
                              }
                            >
                              {b.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    <Button variant="danger" className="w-full justify-center" loading={busy} disabled={onBreak} onClick={() => run(checkOut, 'Checked out.')}>
                      <LogOut className="h-4 w-4" /> Check Out
                    </Button>
                  </div>
                )}

                {checkedOut && (
                  <p className="flex items-center justify-center gap-1.5 py-1 text-body-sm text-on-surface-variant">
                    <Check className="h-4 w-4 text-success-text" /> Done for today. See you tomorrow!
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card-sm border border-outline bg-surface-container-low px-2 py-1.5 text-center">
      <p className="text-[10px] uppercase tracking-wide text-on-surface-variant">{label}</p>
      <p className="truncate text-data-md font-semibold text-on-surface">{value}</p>
    </div>
  );
}
